<template>
    <div v-if="visible" class="recommendation-card" :class="{ 'recommendation-card--embedded': embedded }">

        <!-- Header -->
        <div class="d-flex align-center mb-3">
            <template v-if="!embedded">
                <v-icon small class="mr-2 rec-icon">mdi-robot-outline</v-icon>
                <span class="rec-title">{{ $t('ai.rec_title') }}</span>
                <ai-beta-chip class="ml-2" />
                <v-spacer />
            </template>
            <v-spacer v-else />
            <v-chip x-small class="rec-chip rec-chip--primary ml-2">
                {{ monthLabel }}
            </v-chip>
            <v-btn v-if="!embedded" icon x-small class="ml-1 rec-close" @click="close">
                <v-icon small>mdi-close</v-icon>
            </v-btn>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <!-- Sin recomendación este mes -->
        <div v-else-if="!recommendation" class="rec-empty text-center">
            {{ $t('ai.rec_empty') }}
        </div>

        <div v-else>
            <!-- Badge nueva -->
            <v-chip
                v-if="!recommendation.read"
                x-small
                class="rec-chip rec-chip--new mb-3"
            >
                {{ $t('ai.rec_new') }}
            </v-chip>

            <!-- Resumen narrativo -->
            <p class="rec-text mb-3">{{ recommendation.summary }}</p>

            <v-divider class="mb-3 rec-divider" />

            <!-- Cambios concretos -->
            <div class="rec-section mb-2">{{ $t('ai.rec_changes') }}</div>
            <div
                v-for="(change, i) in recommendation.changes"
                :key="i"
                class="recommendation-change mb-2"
                :class="`change--${change.priority}`"
            >
                <div class="d-flex align-center mb-1">
                    <v-icon x-small class="mr-1 rec-dot">
                        mdi-circle
                    </v-icon>
                    <span class="rec-area">{{ change.area }}</span>
                    <v-spacer />
                    <span class="rec-meta">{{ $t(`ai.priority_${change.priority}`) }}</span>
                </div>
                <span class="rec-text">{{ change.suggestion }}</span>
            </div>

            <ai-assumed-fields-note :fields="recommendation.assumedFields" />
            <ai-beta-note />

            <!-- Footer -->
            <div class="d-flex justify-end mt-3">
                <span class="rec-meta">
                    {{ $t('ai.rec_generated') }} {{ generatedDate }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import {
    loadRecommendation,
    markRecommendationRead,
    currentMonth,
} from '@/utils/AiRecommendations'
import AiAssumedFieldsNote from '@/components/coach/ai/AiAssumedFieldsNote'
import AiBetaChip from '@/components/coach/ai/AiBetaChip'
import AiBetaNote from '@/components/coach/ai/AiBetaNote'

export default {
    name: 'AiRecommendationCard',

    components: { AiAssumedFieldsNote, AiBetaChip, AiBetaNote },

    props: {
        studentId: {
            type: String,
            required: true,
        },
        embedded: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            recommendation: null,
            loading: false,
            visible: true,
        }
    },

    computed: {
        ...mapState(['currentUser']),
        coachId() {
            return this.currentUser?.uid
        },
        monthLabel() {
            const m = currentMonth()
            return this.$moment(m, 'YYYY-MM').format('MMMM YYYY')
        },
        generatedDate() {
            if (!this.recommendation?.generatedAt) return ''
            const ts = this.recommendation.generatedAt
            const date = ts?.toDate ? ts.toDate() : new Date(ts)
            return this.$moment(date).format('DD/MM/YYYY')
        },
    },

    async mounted() {
        await this.load()
    },

    methods: {
        async load() {
            if (!this.coachId || !this.studentId) return
            this.loading = true
            try {
                this.recommendation = await loadRecommendation(this.coachId, this.studentId)
                if (this.recommendation && !this.recommendation.read) {
                    await markRecommendationRead(this.coachId, this.studentId)
                }
            } catch (e) {
                console.warn('[AiRecommendationCard] Error cargando recomendación:', e?.message)
            } finally {
                this.loading = false
            }
        },

        priorityColor(priority) {
            const colors = { high: '#E53935', medium: '#FB8C00', low: '#43A047' }
            return colors[priority] || 'grey'
        },

        close() {
            this.visible = false
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
.recommendation-card {
    padding: 14px 18px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.recommendation-card--embedded {
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
}

.rec-icon {
    color: var(--v-accent-base) !important;
}

.rec-title {
    font-size: var(--fs-section);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.rec-section {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.rec-text {
    font-size: var(--fs-body);
    color: var(--v-txtPrimary-base);
}

.rec-area {
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.rec-meta {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.rec-empty {
    padding: 24px 0;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.rec-divider {
    border-color: var(--v-outline-base) !important;
}

.rec-close {
    color: var(--v-txtMuted-base) !important;
}

.rec-chip {
    height: 20px !important;
    border-radius: 6px !important;
    font-size: 11px !important;
    font-weight: 600;
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.rec-chip--new {
    background: rgba(102, 204, 204, .16) !important;
    border: 1px solid rgba(102, 204, 204, .35);
}

.theme--light .rec-chip--new {
    color: var(--v-accent-base) !important;
}

.recommendation-change {
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-left: 3px solid var(--v-outline2-base);
}

.change--high {
    border-left-color: var(--v-error-base);
    background: linear-gradient(rgba(232, 92, 92, .08), rgba(232, 92, 92, .08)), var(--v-surface2-base);
}

.change--medium {
    border-left-color: #F59E0B;
    background: linear-gradient(rgba(245, 158, 11, .08), rgba(245, 158, 11, .08)), var(--v-surface2-base);
}

.change--low {
    border-left-color: var(--v-success-base);
    background: linear-gradient(rgba(0, 153, 102, .08), rgba(0, 153, 102, .08)), var(--v-surface2-base);
}

.rec-dot {
    color: var(--v-txtMuted-base) !important;
}

.change--high .rec-dot {
    color: var(--v-error-base) !important;
}

.change--medium .rec-dot {
    color: #F59E0B !important;
}

.change--low .rec-dot {
    color: var(--v-success-base) !important;
}
</style>
